import { SafeHtml } from '@angular/platform-browser';
import { TranslationMapKey } from '../enums';
import { IField, IPagedResponse, SortDirection } from '../shared/interfaces';

export interface ITableSettings {
    sort: ISortData;
    page: IPageData;
}

export interface ITableData<RowData extends {} = any> {
    columns?: IField[];
    rowObjects: RowData[];
    pagedResponse: IPagedResponse;
    sortData?: ISortData;
}

export interface ITableRow<RowData extends {} = any> {
    object: RowData;
    cells: ITableBodyCell[];
    actionItems: IRowActionItem[];
}

export interface ITableBodyCell {
    text: SafeHtml;
    field: IField;
    value: any;
    cssClass?: string;
}

export interface ITableHeaderCell {
    text: string;
    cssClass?: string;
}

export interface IPageData {
    offset: number;
    limit: number;
}

export interface ISortData {
    fieldName: string;
    sortDirection: SortDirection;
}

export type IRowActionItemActionFn<RowData extends {}> = (row: RowData) => void;

export interface IRowActionItem<RowData extends {} = any> {
    label: TranslationMapKey | (string & {});
    key: string;
    additionalClassNames?: string,
    actionFn: IRowActionItemActionFn<RowData>;
    disabledFn?: (row: ITableRow<RowData>) => boolean,
}

export type IRowActionObject<RowData extends {} = any> = {
    viewFn?: IRowActionItemActionFn<RowData>
    otherActions?: IRowActionItem<RowData>[]
}

export type IRowGetterWithReturn<RowData extends {} = any, Return extends any = any> = (row: RowData) => Return;

export type IRowActionGetter<RowData extends {} = any> = IRowGetterWithReturn<RowData, IRowActionObject<RowData>>;

export type IRowClassNamesGetter<RowData extends {} = any> = IRowGetterWithReturn<RowData, string[]>;

export const getRowActionItems = <RowData extends {} = any>(row: RowData, getter: IRowActionGetter<RowData> | undefined): IRowActionItem<RowData>[] => {
    if (!getter) {
        return [];
    }
    const actionItems: IRowActionItem[] = [];
    const actionObject = getter(row);

    if (actionObject.viewFn) {
        actionItems.push({
            actionFn: actionObject.viewFn,
            label: 'VIEW',
            key: 'view',
        });
    }
    actionItems.push(...actionObject.otherActions || []);

    return actionItems;
};
